// TanStack Router setup — code-based route tree.
//
// Two root branches:
//   /login          — public, renders without AdminLayout chrome
//   authed/*        — guarded by `beforeLoad` which redirects unauthenticated
//                     visitors to /login with the original path captured
//                     as a `redirect` search param.
//
// All 14 module routes (dashboard + bookings + cars + vendors + 10 stubs)
// hang off the authed branch.

import type { FunctionComponent } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { AdminLayout } from '@/layouts/AdminLayout';
import { authStore } from '@/lib/auth-store';
import { Dashboard } from '@/routes/Dashboard';
import { Inbox } from '@/routes/Inbox';
import { Bookings } from '@/routes/Bookings';
import { BookingDetail } from '@/routes/BookingDetail';
import { Cars } from '@/routes/Cars';
import { CarDetail } from '@/routes/CarDetail';
import { Vendors } from '@/routes/Vendors';
import { VendorDetail } from '@/routes/VendorDetail';
import { Customers } from '@/routes/Customers';
import { CustomerDetail } from '@/routes/CustomerDetail';
import { Locations } from '@/routes/Locations';
import { LocationDetail } from '@/routes/LocationDetail';
import { Reviews } from '@/routes/Reviews';
import { Coupons } from '@/routes/Coupons';
import { TransferPricing } from '@/routes/TransferPricing';
import { Drivers } from '@/routes/Drivers';
import { Rides } from '@/routes/Rides';
import { AirportRequests } from '@/routes/AirportRequests';
import { TransfersHidden } from '@/routes/TransfersHidden';
import { TRANSFERS_UI_ENABLED } from '@/lib/features';
import { Inquiries } from '@/routes/Inquiries';
import { InquiryDetail } from '@/routes/InquiryDetail';
import { Newsletter } from '@/routes/Newsletter';
import { Payouts } from '@/routes/Payouts';
import { PayoutDetail } from '@/routes/PayoutDetail';
import { HostPayoutQueue } from '@/routes/HostPayoutQueue';
import { VendorStatements } from '@/routes/VendorStatements';
import { Support } from '@/routes/Support';
import { Finance } from '@/routes/Finance';
import { Reports } from '@/routes/Reports';
import { Settings } from '@/routes/Settings';
import { AdminUsers } from '@/routes/AdminUsers';
import { Configs } from '@/routes/Configs';
import { AuditLog } from '@/routes/AuditLog';
import { Health } from '@/routes/Health';
import { Templates } from '@/routes/Templates';
import { Attributes } from '@/routes/Attributes';
import { VehicleCatalog } from '@/routes/VehicleCatalog';
import { NeedsLinking } from '@/routes/NeedsLinking';
import { VehicleRequests } from '@/routes/VehicleRequests';
import { Login } from '@/routes/Login';
import { ForgotPassword } from '@/routes/ForgotPassword';
import { ResetPassword } from '@/routes/ResetPassword';
// Disputes + Claims share one backing route file (unified `app.disputes`
// table); Complaints is its own surface (support tickets with SLA/priority).
import { Claims, Disputes } from '@/routes/Disputes';
import { DisputeDetail } from '@/routes/DisputeDetail';
import { Complaints } from '@/routes/Complaints';
import { ComplaintDetail } from '@/routes/ComplaintDetail';
import { Partners } from '@/routes/Partners';
import { PartnerApplications } from '@/routes/PartnerApplications';
import { NotFound } from '@/routes/Stubs';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});

// /login — public, no AdminLayout chrome.
const loginSearchSchema = z.object({ redirect: z.string().optional() });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
  validateSearch: loginSearchSchema,
});

// /forgot-password — public, requests a reset email from Hono.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPassword,
});

// /reset-password?token=<...> — public, consumes the email link and
// auto-logs the operator in once a new password is set.
const resetPasswordSearchSchema = z.object({ token: z.string().optional() });
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPassword,
  validateSearch: resetPasswordSearchSchema,
});

// Protected branch — requires a token, wraps in AdminLayout.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: ({ location }) => {
    if (!authStore.getToken()) {
      throw redirect({
        to: '/login' as never,
        search: { redirect: location.href } as never,
        replace: true,
      });
    }
  },
  component: () => (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' as never });
  },
});

function transfersOnly(component: FunctionComponent): FunctionComponent {
  return TRANSFERS_UI_ENABLED ? component : TransfersHidden;
}

function makeRoute(path: string, component: FunctionComponent) {
  return createRoute({
    getParentRoute: () => authedRoute,
    path,
    component,
  });
}

const dashboardRoute = makeRoute('/dashboard', Dashboard);
const inboxRoute = makeRoute('/inbox', Inbox);
const bookingsRoute = makeRoute('/bookings', Bookings);
const bookingDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/bookings/$reference',
  component: BookingDetail,
});
const carsRoute = makeRoute('/cars', Cars);
const carDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/cars/$id',
  component: CarDetail,
});
const vendorsRoute = makeRoute('/crew', Vendors);
const vendorDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/crew/$id',
  component: VendorDetail,
});
// Crew rebrand: old /vendors* bookmarks 301 -> /crew* (client-side).
const vendorsRedirectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vendors',
  beforeLoad: () => {
    throw redirect({ to: '/crew' as never, replace: true });
  },
});
const vendorDetailRedirectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vendors/$id',
  beforeLoad: ({ params }: { params: { id: string } }) => {
    throw redirect({ to: '/crew/$id' as never, params: { id: params.id } as never, replace: true });
  },
});
const customersRoute = makeRoute('/customers', Customers);
const customerDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/customers/$id',
  component: CustomerDetail,
});
const locationsRoute = makeRoute('/locations', Locations);
const locationDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/locations/$id',
  component: LocationDetail,
});
const reviewsRoute = makeRoute('/reviews', Reviews);
const inquiriesRoute = makeRoute('/inquiries', Inquiries);
const inquiryDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/inquiries/$id',
  component: InquiryDetail,
});
const supportRoute = makeRoute('/support', Support);
const disputesRoute = makeRoute('/disputes', Disputes);
const disputeDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/disputes/$id',
  component: DisputeDetail,
});
const complaintsRoute = makeRoute('/complaints', Complaints);
const complaintDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/complaints/$id',
  component: ComplaintDetail,
});
const couponsRoute = makeRoute('/pricing', Coupons);
// Transfer screens stay routable (an ops-shared link must not 404) but show
// a notice while the product is off — see lib/features.ts.
const transferPricingRoute = makeRoute('/transfer-pricing', transfersOnly(TransferPricing));
const driversRoute = makeRoute('/drivers', transfersOnly(Drivers));
const ridesRoute = makeRoute('/rides', transfersOnly(Rides));
const airportRequestsRoute = makeRoute('/airport-requests', transfersOnly(AirportRequests));
const newsletterRoute = makeRoute('/newsletter', Newsletter);
const claimsRoute = makeRoute('/claims', Claims);
const payoutsRoute = makeRoute('/payouts', Payouts);
const payoutDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/payouts/$id',
  component: PayoutDetail,
});
const hostPayoutsRoute = makeRoute('/host-payouts', HostPayoutQueue);
// ?vendorId=<id> deep-links to one crew's statements (used by the crew
// detail page's "Supplier commission statements" section).
const vendorStatementsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vendor-statements',
  component: VendorStatements,
  validateSearch: z.object({
    vendorId: z.coerce.number().int().positive().optional(),
  }),
});
const financeRoute = makeRoute('/finance', Finance);
const reportsRoute = makeRoute('/reports', Reports);
const settingsRoute = makeRoute('/settings', Settings);
const adminUsersRoute = makeRoute('/admin-users', AdminUsers);
const configsRoute = makeRoute('/configs', Configs);
const auditLogRoute = makeRoute('/audit-log', AuditLog);
const partnersRoute = makeRoute('/partners', Partners);
const partnerApplicationsRoute = makeRoute('/partner-applications', PartnerApplications);
const templatesRoute = makeRoute('/templates', Templates);
const attributesRoute = makeRoute('/attributes', Attributes);
// ?entry=<id> deep-links straight to one catalog entry's editor (used by the
// car-detail "manage photos on the catalog Car" panel).
const vehicleCatalogRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vehicle-catalog',
  component: VehicleCatalog,
  validateSearch: z.object({ entry: z.coerce.number().int().positive().optional() }),
});
const needsLinkingRoute = makeRoute('/needs-linking', NeedsLinking);
const vehicleRequestsRoute = makeRoute('/vehicle-requests', VehicleRequests);
const healthRoute = makeRoute('/health', Health);

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authedRoute.addChildren([
    indexRoute,
    dashboardRoute,
    inboxRoute,
    bookingsRoute,
    bookingDetailRoute,
    carsRoute,
    carDetailRoute,
    vendorsRoute,
    vendorDetailRoute,
    vendorsRedirectRoute,
    vendorDetailRedirectRoute,
    customersRoute,
    customerDetailRoute,
    locationsRoute,
    locationDetailRoute,
    reviewsRoute,
    inquiriesRoute,
    inquiryDetailRoute,
    supportRoute,
    disputesRoute,
    disputeDetailRoute,
    complaintsRoute,
    complaintDetailRoute,
    couponsRoute,
  transferPricingRoute,
  driversRoute,
  ridesRoute,
  airportRequestsRoute,
    newsletterRoute,
    claimsRoute,
    payoutsRoute,
    payoutDetailRoute,
    hostPayoutsRoute,
    vendorStatementsRoute,
    financeRoute,
    reportsRoute,
    settingsRoute,
    adminUsersRoute,
    configsRoute,
    auditLogRoute,
    partnersRoute,
    partnerApplicationsRoute,
    templatesRoute,
    attributesRoute,
    vehicleCatalogRoute,
    needsLinkingRoute,
    vehicleRequestsRoute,
    healthRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
